import { ChangeEvent, FC, useEffect, useLayoutEffect, useState } from 'react';

import { PaletteOutlined, UpdateOutlined } from '@mui/icons-material';
import {
  Box,
  Divider,
  Paper,
  Popover,
  PopoverVirtualElement,
  Stack,
  Tab,
  Tabs,
  Typography,
  useColorScheme
} from '@mui/material';
import useSettingsStore from 'app/store/useSettingsStore';

import { CloseIcon } from 'shared/assets/icons';
import { StyledSwitch } from 'shared/ui/switch-styled';
import { addMessageToast } from 'shared/utils';

import LightModeIcon from '../../shared/assets/icons/Light_mode.svg';
import DarkModeIcon from '../../shared/assets/icons/dark_mode.svg';

interface IProps {
  open: boolean;
  anchorEl:
    | Element
    | (() => Element)
    | PopoverVirtualElement
    | (() => PopoverVirtualElement)
    | null
    | undefined;
  handleClose: () => void;
}

const SettingsPopover: FC<IProps> = ({ open, anchorEl, handleClose }) => {
  const [value, setValue] = useState<number | undefined>();
  const { testMode, toogleTestMode, darkTheme, toogleTheme } = useSettingsStore();
  const { mode, setMode, colorScheme, setColorScheme } = useColorScheme();

  const handleSwitch = async (event?: ChangeEvent<HTMLInputElement>) => {
    const isChecked = event?.target.checked;
    toogleTestMode(!!isChecked);
  };
  const handleTabChange = (_event: React.SyntheticEvent | undefined, newValue: any) => {
    setValue(newValue);
  };

  useLayoutEffect(() => {
    toogleTheme(colorScheme === 'dark');
  }, [colorScheme]);

  useEffect(() => {
    if (open)
      addMessageToast(`New version of table ${testMode ? 'included' : 'excluded'}`, 'success');
  }, [testMode]);

  useEffect(() => {
    if (open) addMessageToast(`${darkTheme ? 'Dark' : 'Light'} theme enabled`, 'success');
  }, [darkTheme]);

  const tabData = [
    {
      label: 'Interface',
      content: (
        <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
          <Typography component={'p'} fontSize={'18px'} lineHeight={'21px'}>
            Theme
          </Typography>
          <Box>
            <StyledSwitch
              value={darkTheme}
              setValue={e => {
                toogleTheme(e?.target.checked);
                // setMode(e?.target.checked ? 'dark' : 'light');
                setColorScheme(e?.target.checked ? 'dark' : 'light');
              }}
              sx={{
                width: '68px',
                height: '28px',
                borderRadius: '1000px',
                margin: '0px',
                boxShadow: 'none',
                border: darkTheme ? '1px solid rgba(68, 70, 79, 0.20) !important' : 'none',
                '&:after': {
                  content: darkTheme ? `'Dark'` : `'Light'`,
                  position: 'absolute',
                  transform: 'translate(0%, -50%)',
                  top: '50%',
                  left: darkTheme ? '10px' : 'none',
                  right: darkTheme ? 'none' : '9px',
                  color: darkTheme ? 'rgba(255, 255, 255, 0.94)' : '#1D1B20',
                  textTransform: 'uppercase',
                  fontSize: '10px',
                  fontStyle: 'normal',
                  fontWeight: '700',
                  lineHeight: 'normal',
                  letterSpacing: '0.15px',
                  transition: 'all ease-in-out .3s'
                },
                '.MuiSwitch': {
                  '&-input': {
                    height: '28px',
                    left: darkTheme ? '-200%' : '0'
                  },
                  '&-switchBase': {
                    margin: darkTheme ? '2px' : '3px',
                    '&.Mui-checked': {
                      transform: 'translateX(40px)'
                    }
                  },
                  '&-track': {
                    backgroundColor: darkTheme
                      ? 'var(--mui-palette-background-paper) !important'
                      : '#E6E6E6 !important',
                    transition: 'all ease-in-out .3s',
                    '&:hover': {
                      backgroundColor: darkTheme
                        ? 'var(--mui-palette-background-paper)'
                        : '#E6E6E6 !important'
                    },
                    '&:active': {
                      backgroundColor: darkTheme
                        ? 'var(--mui-palette-background-paper)'
                        : '#E6E6E6 !important'
                    }
                  },
                  '&-thumb': {
                    width: '22px',
                    height: '22px',
                    backgroundColor: '#fff !important',
                    backgroundImage: darkTheme ? `url(${DarkModeIcon})` : `url(${LightModeIcon})`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: '50% 50%',
                    transition: 'all ease-in-out .6s',
                    '&:hover': {
                      backgroundColor: '#fff !important'
                    },
                    '&:active': {
                      backgroundColor: '#fff !important'
                    }
                  }
                }
              }}
            />
          </Box>
        </Box>
      ),
      icon: <PaletteOutlined />
    },
    {
      label: 'Update',
      content: (
        <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
          <Typography component={'p'} fontSize={'18px'} lineHeight={'21px'}>
            Include new table version
          </Typography>
          <Box>
            <StyledSwitch
              value={testMode}
              setValue={e => {
                handleSwitch(e);
              }}
            />
          </Box>
        </Box>
      ),
      icon: <UpdateOutlined />
    }
  ];

  return (
    <Popover
      open={open}
      onClose={() => {
        handleClose();
        handleTabChange(undefined, undefined);
      }}
      anchorEl={anchorEl}
      elevation={4}
      hideBackdrop={false}
      sx={{
        '&>.MuiPaper-root': {
          border: '1px solid',
          borderColor: darkTheme ? 'var(--mui-palette-divider)' : 'transparent',
          borderTopLeftRadius: '16px',
          borderTopRightRadius: '16px',
          borderBottomRightRadius: '16px',
          marginLeft: '8px'
        },
        '.MuiDivider-root': {
          background: 'var(--mui-palette-divider)',
          color: 'var(--mui-palette-divider)',
          margin: '24px 0',
          width: value === undefined ? '0' : '1px !important'
        }
      }}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right'
      }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
      <Box
        sx={{
          display: 'block',
          position: 'absolute',
          top: '16px',
          right: '17px',
          width: 25,
          height: 25,
          color: 'var(--mui-palette-text-primary)',
          cursor: 'pointer'
        }}
        onClick={handleClose}>
        <CloseIcon color={'var(--mui-palette-modal-closeIcon)'} />
      </Box>
      <Stack direction='row' minHeight='260px'>
        <Box sx={{ minWidth: '218px', display: 'flex', flexDirection: 'column' }}>
          <Typography
            component={'p'}
            sx={{
              color: 'var(--mui-palette-primary-main)',
              fontSize: '26px',
              fontFamily: 'Roboto',
              fontWeight: '500',
              lineHeight: '36px',
              wordWrap: 'break-word',
              p: '12px 24px',
              textTransform: 'none'
            }}>
            Settings
          </Typography>
          <Tabs
            key={open.toString()}
            orientation='vertical'
            sx={{
              '.MuiTabs-indicator': { display: 'none' }
            }}
            value={value}
            onChange={handleTabChange}>
            {tabData.map((tab, index) => (
              <Tab
                key={index}
                sx={{
                  p: 0,
                  pl: '24px',
                  alignItems: 'flex-start',
                  color: 'var(--mui-palette-primary-main)',
                  '&:hover': {
                    background: 'rgba(52, 121, 230, 0.14)'
                  },
                  '&:active': {
                    background: 'rgba(28, 105, 225, 0.18)'
                  },
                  '&.Mui-selected': {
                    color: 'var(--mui-palette-primary-main)',
                    background: 'rgba(28, 105, 225, 0.18)'
                  }
                }}
                label={
                  <Box>
                    <Stack direction='row' gap={1}>
                      {tab.icon}
                      <Typography
                        whiteSpace='nowrap'
                        sx={{
                          textAlign: 'left',
                          fontFamily: 'Roboto',
                          fontSize: '18px',
                          fontStyle: 'normal',
                          fontWeight: '500',
                          lineHeight: 'normal',
                          textTransform: 'none'
                        }}>
                        {tab.label}
                      </Typography>
                    </Stack>
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Box>
        <Divider />
        {tabData.map(
          (tab, index) =>
            value === index && (
              <Paper sx={{ minWidth: '322px' }} elevation={0}>
                <Typography
                  component={'p'}
                  sx={{
                    color: 'var(--mui-palette-primary-main)',
                    fontSize: '26px',
                    fontFamily: 'Roboto',
                    fontWeight: '500',
                    lineHeight: '36px',
                    wordWrap: 'break-word',
                    p: '12px 24px'
                  }}>
                  {tab.label}
                </Typography>
                <Box
                  sx={{
                    p: '12px 24px',
                    color: 'var(--mui-palette-text-primary)',
                    fontFamily: 'Roboto',
                    fontSize: '18px',
                    fontStyle: 'normal',
                    fontWeight: '500',
                    lineHeight: 'normal'
                  }}>
                  {tab.content}
                </Box>
              </Paper>
            )
        )}
      </Stack>
    </Popover>
  );
};

export default SettingsPopover;
